import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Star } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { EASE } from "./Reveal";
import { whatsappUrl } from "@/data/company";
import { PRIMARY_LISTING } from "@/data/reviews";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src="/catalog/hero-entrance.jpg"
          alt="Polished Kota stone floor at a residence entrance by Garg Stone Company"
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/35" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex min-h-screen flex-col justify-end px-6 md:px-12 lg:px-20 pb-14 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-3"
        >
          <p className="text-xs tracking-[0.35em] uppercase font-heading font-medium text-bone/70" data-testid="hero-overline">
            Kota Stone Manufacturer & Supplier — Kota, Since 1988
          </p>
          <button
            onClick={() => scrollToSection("#reviews")}
            aria-label={`Rated ${PRIMARY_LISTING.score} on Google from ${PRIMARY_LISTING.count} reviews — see reviews`}
            data-testid="hero-rating-badge"
            className="inline-flex items-center gap-2 border border-copper/50 bg-ink/40 backdrop-blur-md px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-heading font-bold text-bone/85 transition-colors duration-300 hover:border-copper hover:text-copper"
          >
            <Star size={12} className="text-copper" fill="currentColor" strokeWidth={0} />
            {PRIMARY_LISTING.score} · {PRIMARY_LISTING.count} Google reviews
          </button>
        </motion.div>

        <h1
          className="hero-title font-heading font-black uppercase leading-[0.92]"
          data-testid="hero-heading"
        >
          <Line delay={0.35}>Grounding</Line>
          <Line delay={0.5}>
            <span className="font-editorial italic font-medium normal-case text-copper">spaces</span>
          </Line>
          <Line delay={0.65}>naturally.</Line>
        </h1>

        <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="max-w-md text-sm md:text-base font-light leading-relaxed text-bone/70"
            data-testid="hero-subtext"
          >
            From our quarries and factories in Kota, Rajasthan, Garg Stone Company
            has mined, finished and supplied natural stone for homes, industries
            and institutions across India for over three decades.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#products")}
              data-testid="hero-explore-button"
              className="group relative overflow-hidden bg-bone text-ink px-8 py-4 text-xs tracking-[0.25em] uppercase font-heading font-bold"
            >
              <span className="relative z-10 transition-colors duration-300 group-hover:text-bone">Explore the Stone</span>
              <span className="absolute inset-0 bg-copper translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0" />
            </button>
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with us on WhatsApp"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 border border-bone/30 px-8 py-4 text-xs tracking-[0.25em] uppercase font-heading font-bold transition-colors duration-300 hover:border-copper hover:text-copper"
            >
              <MessageCircle size={15} /> WhatsApp Us
            </a>
          </motion.div>
        </div>

        <motion.button
          onClick={() => scrollToSection("#origins")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-14 right-6 md:right-12 hidden lg:flex flex-col items-center gap-3 text-bone/50 transition-colors duration-300 hover:text-bone"
          aria-label="Scroll to story"
          data-testid="hero-scroll-cue"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase font-heading rotate-90 origin-center translate-y-[-8px]">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
            <ArrowDown size={16} />
          </motion.span>
        </motion.button>
      </motion.div>
    </section>
  );
};

export default Hero;
